// src/pages/AFSAAllCampaigns.jsx
import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axios";

import SpecialCampaignSkeleton from "../Campaign/SpecialCampaignSKeleton";
import PrimaryButton from "../../common/PrimaryButton";
import { useNavigate } from "react-router-dom";

const SpecialCampaign = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();

  const getCampaignData = async () => {
    try {
      const response = await axiosInstance.get("/campaigns/all-campaigns");

      setCampaigns(response.data);
      setFilteredCampaigns(response.data);
      setError(false);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCampaignData();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const significantFiltered = filteredCampaigns.filter((c) => c.isSignificant);

  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 3000);

    return () => clearInterval(interval);
  }, [significantFiltered]);

  const nextSlide = () => {
    setCurrentIndex((prev) =>
      prev === significantFiltered.length - 1 ? 0 : prev + 1,
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? significantFiltered.length - 1 : prev - 1,
    );
  };

  const normalFiltered = filteredCampaigns.filter((c) => !c.isSignificant);

  if (loading) return <SpecialCampaignSkeleton />;

  if (error)
    return (
      <div className="text-center py-20 text-red-500 text-lg">
        Failed to load campaigns
      </div>
    );

  return (
    <div className="w-full bg-gray-50 px-3 sm:px-6 md:px-10 py-6 md:py-10">
      {significantFiltered.length > 0 && (
        <div className="relative border border-gray-200 shadow-xl rounded-xl p-4 sm:p-6 md:p-10 bg-white">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500"
              style={{
                transform: `translateX(-${currentIndex * 100}%)`,
              }}
            >
              {significantFiltered.map((campaign) => (
                <div
                  onClick={() => navigate(`/campaigns/${campaign._id}`)}
                  key={campaign._id}
                  className="min-w-full"
                >
                  <div className="flex flex-col md:flex-row gap-4 md:gap-10 bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition h-full">
                    {/* Image */}
                    <div className="w-full md:w-1/2 flex-shrink-0">
                      <div className="w-full aspect-[4/3] sm:aspect-[16/9] md:aspect-auto md:h-[320px] lg:h-[480px] overflow-hidden">
                        <img
                          src={campaign.images?.[0]}
                          alt={campaign.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4 flex flex-col justify-between w-full md:w-1/2 h-full">
                      <div className="flex flex-col h-full">
                        <div className="flex items-start">
                          {campaign.isSpecial && campaign.logo && (
                            <img
                              src={campaign.logo}
                              alt="campaign logo"
                              className="w-28 object-cover"
                            />
                          )}
                        </div>
                        {/* Title */}
                        <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold mb-2 line-clamp-1 mt-2">
                          {campaign.name}
                        </h3>

                        {/* Description */}
                        <div
                          className="
    text-gray-600 
    text-sm sm:text-base 
    leading-relaxed 
    tracking-wide 
    line-clamp-4
    md:line-clamp-[10] 
    flex-grow
    [&>p]:mb-2
    [&>p]:leading-relaxed
  "
                          dangerouslySetInnerHTML={{
                            __html: campaign.description,
                          }}
                        />
                        {/* Button */}
                        <div className="mt-4 md:mt-6">
                          <PrimaryButton
                            text="Donate"
                            onClick={() =>
                              navigate(`/campaigns/${campaign._id}`)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Prev Button */}
          <button
            onClick={prevSlide}
            className="absolute top-1/2 left-1 sm:left-2 -translate-y-1/2 bg-black text-white px-2 sm:px-3 py-1 rounded-full text-sm sm:text-base"
          >
            ‹
          </button>

          {/* Next Button */}
          <button
            onClick={nextSlide}
            className="absolute top-1/2 right-1 sm:right-2 -translate-y-1/2 bg-black text-white px-2 sm:px-3 py-1 rounded-full text-sm sm:text-base"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
};

export default SpecialCampaign;
